import { useAtomValue } from 'jotai';
import { InlineNotification } from '@carbon/react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';

import { useCreatePartiallyCoveredWellsAtom } from './atoms';
import { PlainLink } from 'components/link';
import { DataTable } from 'components/data-table';
import { formatCoveragePercent } from 'utils/number';
import type {
  UnassignedWell,
  UnassignedWellUnit,
} from 'types/packages-api/responses';
import styles from './partially-covered-wells.module.scss';

type UnassignedWellRow = UnassignedWell & Record<string, unknown>;

const columnHelper = createColumnHelper<UnassignedWellRow>();

const columns = [
  columnHelper.accessor('uwi', {
    id: 'uwi',
    header: 'UWI',
    size: 150,
    cell: (info) => (
      <PlainLink
        href={info.row.original.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {info.getValue()}
      </PlainLink>
    ),
  }),
  columnHelper.accessor('holeDirection', {
    id: 'holeDirection',
    header: 'Hole Direction',
    size: 130,
    meta: { type: 'text' },
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Well Status',
    size: 120,
    meta: { type: 'text' },
  }),
  columnHelper.accessor('units', {
    id: 'units',
    header: 'Units (Coverage %)',
    size: 250,
    meta: {
      getExportValue: (row: UnassignedWellRow) => {
        const units = row.units;
        if (!units || units.length === 0) return '—';
        return units
          .map((u) => `${u.name} (${formatCoveragePercent(u.coverageRatio)})`)
          .join(', ');
      },
    },
    cell: (info) => {
      const units = info.getValue() as UnassignedWellUnit[];
      if (!units || units.length === 0) return '—';
      return (
        <div className={styles.unitsList}>
          {units.map((u, idx) => (
            <span key={u.name}>
              {idx > 0 && ', '}
              <PlainLink href={u.href} target="_blank" rel="noreferrer">
                {u.name}
              </PlainLink>
              {` (${formatCoveragePercent(u.coverageRatio)})`}
            </span>
          ))}
        </div>
      );
    },
  }),
  columnHelper.accessor((row) => row.assignment?.developmentAreaName ?? '', {
    id: 'assignedUnit',
    header: 'Assigned Unit',
    size: 150,
    meta: { type: 'text' },
  }),
  columnHelper.accessor((row) => row.assignment?.assignmentType ?? '', {
    id: 'assignmentType',
    header: 'Assignment Type',
    size: 140,
    cell: (info) => {
      const type = info.getValue() as string;
      if (!type) return '—';
      return type.charAt(0).toUpperCase() + type.slice(1);
    },
  }),
];

const PartiallyCoveredWells = ({
  href,
  count,
}: {
  href: string;
  count?: number | null;
}) => {
  const partiallyCoveredWellsAtom = useCreatePartiallyCoveredWellsAtom(href);
  const partiallyCoveredWells = useAtomValue(partiallyCoveredWellsAtom);

  const data = partiallyCoveredWells.data;

  if (data && 'error' in data)
    return <InlineNotification kind="error" title={data.error} lowContrast />;

  if (!data)
    return (
      <DataTable
        data={[] as UnassignedWellRow[]}
        columns={columns as ColumnDef<UnassignedWellRow>[]}
        getRowId={(row) => row.uwi}
        isLoading={true}
        persistenceKey="evaluation-result-unassigned-wells"
        config={{
          sorting: true,
          globalSearch: true,
          columnFilters: true,
          columnResizing: true,
          export: false,
        }}
      />
    );

  return (
    <div>
      {!!count && (
        <InlineNotification
          kind="error"
          title={`WARNING: there are ${count} Partially Covered Wells which will not be valued by Eval 3.0. Please reach out to GIS to have correct Units created for these Wells.`}
          lowContrast
          hideCloseButton
        />
      )}
      <DataTable
        data={data.wells as UnassignedWellRow[]}
        columns={columns as ColumnDef<UnassignedWellRow>[]}
        getRowId={(row) => row.uwi}
        persistenceKey="evaluation-result-unassigned-wells"
        config={{
          sorting: true,
          globalSearch: true,
          columnFilters: true,
          columnResizing: true,
          export: {
            exportPresets: [
              {
                name: 'Export',
                format: 'csv',
                filename: 'partially-covered-wells',
              },
            ],
          },
        }}
      />
    </div>
  );
};

export { PartiallyCoveredWells };
